// The supervisor's Resume (DC-07, ADR-0019 §5; `POST runs/{id}/resume`; AGT-UI-2): a run the
// verdict gate parked (`waiting_input` after a missing, not-verified or inconclusive verdict) or a
// premise escalation stopped goes back to `running`; a verdict it gave is cleared into its decision
// log with the note (otherwise the note is kept on the resume event). One component for the run page and the inbox's review item, so both resume the
// same way. The server decides who may (the run's supervisor, or a company owner / admin); a
// refusal is shown in the dialog.
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PlayIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { RunRow } from "../../api/types";
import type { CompanyAccess } from "../company";
import { problemText } from "../de/de-status";
import { invalidateDecided } from "./agent-queries";

/** `REVIEW_REASONS` of services/api/src/domain/runs/verdict.ts: why a run waits for review. */
export const REVIEW_REASON_LABELS: Record<string, string> = {
  verdict_missing: "It ended without a verdict",
  verdict_not_verified: "Its verdict was not verified",
  verdict_inconclusive: "Its verdict was inconclusive",
  premise_escalated: "It questioned an assumption after two failed fixes",
};

/** A run waits for its supervisor: parked by the verdict gate or by a premise escalation. */
export function needsReview(run: Pick<RunRow, "state" | "state_reason">): boolean {
  return (
    run.state === "waiting_input" && !!run.state_reason && run.state_reason in REVIEW_REASON_LABELS
  );
}

/**
 * Who is offered Resume: a member of the company (the server allows the run's supervisor and the
 * owners and admins). A read-only client viewer or an agency operator without a membership is not.
 */
export function mayOfferResume(company: CompanyAccess): boolean {
  return company.roles.some((r) => r !== "client_viewer" && r !== "agency_operator");
}

export function ResumeRunButton({
  api,
  co,
  runId,
  size = "default",
  onResumed,
}: {
  api: Api;
  co: string;
  runId: string;
  size?: "xs" | "sm" | "default" | "lg";
  onResumed?: (run: RunRow) => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const id = useId();
  const resume = useMutation({
    mutationFn: () =>
      api.client.runs.resume({
        params: { co, id: runId },
        body: note.trim() ? { note: note.trim() } : {},
      }),
    onSuccess: (run) => {
      setOpen(false);
      setNote("");
      onResumed?.(run as RunRow);
    },
    onSettled: () => invalidateDecided(qc, co),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size={size} onClick={() => setOpen(true)} data-slot="resume-run">
        <PlayIcon aria-hidden="true" />
        Resume
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resume the run?</DialogTitle>
          <DialogDescription>
            The run goes back to work. A verdict it gave is cleared into its decision log with your
            note; otherwise the note is kept with the resume.
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor={id}>Note (optional)</FieldLabel>
          <Textarea
            id={id}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="The export is sorted by date, not stage; regroup by date."
          />
        </Field>
        {resume.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(resume.error, "The run could not be resumed.")}
          </p>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button disabled={resume.isPending} onClick={() => resume.mutate()}>
            {resume.isPending ? "Resuming…" : "Resume"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
